import TextField, { TextFieldVariants } from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { colors } from "../../constants/colors";
import HelperText from "./helperText";
import { FormHelperText, Typography } from "@mui/material";
import React, { useEffect, useRef } from "react";

const CustomTextField = styled(TextField)<{ inputHeight?: any; borderColor?: string }>(
    ({ inputHeight, borderColor }) => ({
        width: "100%",

        /* root: อย่าบังคับ height เวลาเป็น multiline */
        ".MuiOutlinedInput-root": {
            borderRadius: 6,
            "&:not(.Mui-disabled)": { backgroundColor: colors.white },
            ".MuiOutlinedInput-notchedOutline, &.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                borderColor: borderColor || `${colors.black_12}`,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: borderColor || `${colors.black_12}`,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: borderColor || colors.themeMainColor,
            },
            "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                border: `2px solid ${colors.red} !important`,
            },
            ".MuiOutlinedInput-notchedOutline": { borderWidth: "1px !important" },
        },

        /* === ความสูงเฉพาะ input ธรรมดา (ไม่ใช่ textarea) === */
        ".MuiOutlinedInput-root:not(.MuiInputBase-multiline)": {
            height: inputHeight || 43,
            display: "flex",
            alignItems: "center",
        },
        ".MuiOutlinedInput-root:not(.MuiInputBase-multiline) .MuiOutlinedInput-input": {
            height: inputHeight || 43,
            padding: "0 1rem",
        },

        /* === textarea: ปล่อยให้ autosize จัดการเอง ห้ามตั้ง height === */
        "textarea.MuiOutlinedInput-input": {
            height: "auto !important",
            padding: "8px 1rem",
            lineHeight: 1.5,
            overflow: "auto",
        },

        /* ของเดิมคงไว้ แต่ไม่ไปบังคับ textarea */
        ".MuiInputLabel-root, .MuiInputLabel-root span": {
            fontSize: 16,
            color: `${colors.black_60} !important`,
            "&.MuiInputLabel-root": { paddingLeft: 1 },
            backgroundColor: "transparent !important",
            "&:before": {
                content: '""',
                position: "absolute",
                width: "100%",
                height: 3,
                top: "50.5%",
                zIndex: -1,
            },
            '&[data-shrink="true"]': {
                "&.Mui-focused": { color: `${colors.themeMainColor} !important` },
                "&:before": { backgroundColor: colors.white },
                span: { color: `${colors.red} !important` },
            },
        },

        ".MuiOutlinedInput-input": {
            fontSize: 16,
            color: `${colors.textPrimary} !important`,
            textOverflow: "ellipsis",
            "-webkit-text-fill-color": "unset !important",
            "&.Mui-disabled": { color: `${colors.black_60} !important` },
            "&::placeholder": { opacity: 1, color: `${colors.placeholder} !important` },
        },

        "&.filed-error .MuiInputLabel-root[data-shrink='true'], &.filed-error .MuiInputLabel-root[data-shrink='true'] span": {
            color: `${colors.red} !important`,
        },

        "input[type=number]": { MozAppearance: "textfield", "-moz-appearance": "textfield" },
        "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": { marginLeft: "0.5rem" },
        "&.hide-control input[type=number], &.hide-control input::-webkit-outer-spin-button, &.hide-control input::-webkit-inner-spin-button": {
            "-webkit-appearance": "none",
            appearance: "none",
            marginLeft: 0,
        },
        ".MuiFormHelperText-root": { marginLeft: 0 },
    })
);


export type InputProps = {
    onFocus?: (value: any) => void;
    onchange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onkeyup?: (value: any) => void;
    onkeypress?: (value: any) => void;
    onblur?: (value: any) => void;
    onClick?: (e: any) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    required?: boolean;
    disabled?: boolean;
    variant?: TextFieldVariants;
    size?: "medium" | "small";
    label?: any;
    value?: any;
    type?: string;
    helperText?: any;
    key?: any | "input-text";
    name?: any;
    id?: any;
    hookValue?: boolean;
    hideControl?: boolean;
    inputProps?: any;
    InputLabelProps?: any;
    inputTextAlign?: "left" | "center" | "right";
    style?: any;
    params?: any;
    inputHeight?: any;
    placeholder?: string;
    disabledAutoComplete?: boolean;
    heading?: string;
    headingAlign?: React.CSSProperties["textAlign"];
    multiline?: boolean;
    maxRows?: any;
    row?: any;
    headingNormal?: boolean;
    disableColor?: string;
    disablePaddingMultiline?: boolean;
    pattern?: string;
    endAdornment?: any;
    startAdornment?: React.ReactNode;
    minRow?: number;
    onInvalid?: (value: any) => void;
    markRequired?: string;
    maxLength?: number;
    enableCountMaxLength?: boolean;
    fullWidth?: boolean;
    borderColor?: string;
    disableUnderline?: boolean;
    noBorder?: boolean;
    enableTextArea?: boolean;
};

export default function InputTextField(props: InputProps) {
    const shrink = props.value ? { shrink: true } : "";
    // ใช้ ref เฉพาะกับ element input จริง ๆ
    const inputElRef = useRef<HTMLInputElement | null>(null);
    const didScrollForThisError = useRef(false);
    const rafRef = useRef<number | null>(null);

    // เรียก scrollIntoView/focus "ครั้งเดียว" เมื่อ helperText โผล่ (และไม่ disabled)
    useEffect(() => {
        if (!props.helperText || props.disabled) {
            didScrollForThisError.current = false;
            return;
        }
        if (didScrollForThisError.current) return;
        didScrollForThisError.current = true;

        rafRef.current = requestAnimationFrame(() => {
            const el = inputElRef.current;
            if (!el) return;
            // ถ้าฟอร์มอยู่ใน container ที่มี auto layout, ดีเฟอร์อีกเฟรมช่วยลด layout shift
            requestAnimationFrame(() => {
                try {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                    el.focus({ preventScroll: true });
                } catch {
                    /* noop */
                }
            });
        });

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [props.helperText, props.disabled]);

    return (
        <>
            {props.heading && (
                <div className="pb-2" style={{ textAlign: props.headingAlign || "left" }}>
                    <Typography variant={props.headingNormal ? "subtitle1" : "body1"}>
                        {props.heading}{" "}
                        {props.required || props.markRequired ? (
                            <span style={{ color: colors.red }}>*</span>
                        ) : (
                            ""
                        )}
                    </Typography>
                </div>
            )}
            <CustomTextField
                noBorder={props.noBorder}
                onClick={props.onClick}
                fullWidth={props.fullWidth}
                // อย่าใส่ ref ที่ TextField root อีก (จะเป็น div) — ใช้ inputRef ด้านล่างแทน
                inputRef={(input: HTMLInputElement | null) => {
                    inputElRef.current = input;
                }}
                inputHeight={props.inputHeight}
                id={props.id}
                key={`text-field-${props.key}`}
                sx={{
                    ".MuiOutlinedInput-root": {
                        backgroundColor: props.disabled
                            ? props.disableColor
                                ? props.disableColor
                                : colors.black_12
                            : "",
                    },
                    ...props.style,
                    "& .MuiOutlinedInput-input": {
                        padding: props.disablePaddingMultiline && "0",
                    },
                }}
                inputProps={{
                    ...{ style: { textAlign: props.inputTextAlign || "left" } },
                    ...props.inputProps,
                    maxLength: props.maxLength,
                    autoComplete: props.disabledAutoComplete ? "off" : "on",
                    pattern: props.pattern,
                }}
                InputLabelProps={{
                    ...props.InputLabelProps,
                    key: props.key,
                    ...shrink,
                }}
                className={`${props.helperText ? "filed-error" : ""} ${props.hideControl ? "hide-control" : ""
                    }`}
                InputProps={{
                    endAdornment: props.endAdornment,
                    startAdornment: props.startAdornment,
                    disableUnderline: props.disableUnderline,
                }}
                onInvalid={props.onInvalid}
                onFocus={props.onFocus}
                onChange={props.onchange || props.onChange}
                onBlur={props.onblur}
                onKeyUp={props.onkeyup}
                onKeyDown={props.onKeyDown}
                onKeyPress={props.onkeypress}
                variant={props.variant || "outlined"}
                required={props.required}
                name={props.name}
                value={props.value}
                label={props.label}
                disabled={props.disabled}
                multiline={props.multiline}
                maxRows={props.maxRows}
                minRows={props.minRow}
                rows={props.row}
                type={props.type || "text"}
                error={props.helperText !== "" && props.helperText !== undefined}
                {...props.params}
                size={props.size || "small"}
                placeholder={props?.placeholder || ""}
                helperText={props.helperText ? <HelperText label={props.helperText} /> : ""}
                borderColor={props.borderColor}
            />
            {props.enableCountMaxLength && props.maxLength && (
                <FormHelperText sx={{ textAlign: "end" }} id="standard-weight-helper-text">
                    {`${(props.value?.length ?? 0)} / ${props.maxLength}`}
                </FormHelperText>
            )}
        </>
    );
}
